import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiEye, FiToggleLeft, FiToggleRight, FiTrash2 } from 'react-icons/fi';
import { getSchemes, getScheme, createScheme, updateScheme, toggleScheme, addCatalogItem, updateCatalogItem, deleteCatalogItem } from '../services/api';

const Schemes: React.FC = () => {
  const [schemes, setSchemes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [showDetail, setShowDetail] = useState<any>(null);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<any>(null);
  const [form, setForm] = useState({ schemeName: '', description: '', startDate: '', endDate: '', bannerImage: '' });
  const [catalogForm, setCatalogForm] = useState({
    schemeId: 0, itemName: '', description: '', imageUrl: '', pointsRequired: 0, stockQty: 0, userType: 'both'
  });

  useEffect(() => { loadSchemes(); }, [page]);

  const loadSchemes = async () => {
    try {
      const res = await getSchemes(page);
      setSchemes(res.data.data);
      setTotal(res.data.totalCount);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ schemeName: '', description: '', startDate: '', endDate: '', bannerImage: '' });
    setShowModal(true);
  };

  const openEdit = (s: any) => {
    setEditing(s);
    setForm({ schemeName: s.schemeName, description: s.description || '', startDate: s.startDate, endDate: s.endDate, bannerImage: s.bannerImage || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updateScheme(editing.schemeId, form);
        toast.success('Scheme updated');
      } else {
        await createScheme(form);
        toast.success('Scheme created');
      }
      setShowModal(false);
      loadSchemes();
    } catch (err: any) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleToggle = async (id: number) => {
    try {
      await toggleScheme(id);
      toast.success('Scheme toggled');
      loadSchemes();
    } catch (err: any) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const viewDetail = async (id: number) => {
    try {
      const res = await getScheme(id);
      setShowDetail(res.data.data);
    } catch (err) { console.error(err); }
  };

  const openAddCatalog = () => {
    setEditingCatalog(null);
    setCatalogForm({ schemeId: showDetail.schemeId, itemName: '', description: '', imageUrl: '', pointsRequired: 0, stockQty: 0, userType: 'both' });
    setShowCatalogModal(true);
  };

  const openEditCatalog = (item: any) => {
    setEditingCatalog(item);
    setCatalogForm({
      schemeId: showDetail.schemeId, itemName: item.itemName, description: item.description || '',
      imageUrl: item.imageUrl || '', pointsRequired: item.pointsRequired, stockQty: item.stockQty, userType: item.userType
    });
    setShowCatalogModal(true);
  };

  const handleSaveCatalog = async () => {
    try {
      if (editingCatalog) {
        await updateCatalogItem(editingCatalog.catalogId, catalogForm);
        toast.success('Item updated');
      } else {
        await addCatalogItem(catalogForm);
        toast.success('Item added');
      }
      setShowCatalogModal(false);
      viewDetail(showDetail.schemeId);
    } catch (err: any) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDeleteCatalog = async (id: number) => {
    if (!window.confirm('Deactivate this catalog item?')) return;
    try {
      await deleteCatalogItem(id);
      toast.success('Item deactivated');
      viewDetail(showDetail.schemeId);
    } catch (err: any) { toast.error('Failed'); }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div className="flex-between mb-20">
        <h3>Schemes</h3>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Create Scheme</button>
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>ID</th><th>Scheme Name</th><th>Start</th><th>End</th><th>Items</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {schemes.map((s) => (
                  <tr key={s.schemeId}>
                    <td>{s.schemeId}</td>
                    <td><strong>{s.schemeName}</strong></td>
                    <td>{s.startDate}</td>
                    <td>{s.endDate}</td>
                    <td>{s.itemCount}</td>
                    <td><span className={`badge ${s.isActive ? 'badge-success' : 'badge-danger'}`}>{s.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="flex gap-10">
                        <button className="btn btn-sm btn-outline" onClick={() => viewDetail(s.schemeId)}><FiEye /></button>
                        <button className="btn btn-sm btn-outline" onClick={() => openEdit(s)}><FiEdit2 /></button>
                        <button className="btn btn-sm btn-outline" onClick={() => handleToggle(s.schemeId)}>
                          {s.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {schemes.length === 0 && <tr><td colSpan={7} className="text-center">No schemes</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Create/Edit Scheme Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Scheme' : 'Create Scheme'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="form-group">
              <label>Scheme Name *</label>
              <input className="form-control" value={form.schemeName} onChange={(e) => setForm({ ...form, schemeName: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date *</label>
                <input className="form-control" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              </div>
              <div className="form-group">
                <label>End Date *</label>
                <input className="form-control" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Banner Image URL</label>
              <input className="form-control" value={form.bannerImage} onChange={(e) => setForm({ ...form, bannerImage: e.target.value })} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>{editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Scheme Detail Modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal" style={{ minWidth: 700 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showDetail.schemeName}</h3>
              <button className="modal-close" onClick={() => setShowDetail(null)}>&times;</button>
            </div>
            <p style={{ marginBottom: 5 }}>{showDetail.description}</p>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 15 }}>{showDetail.startDate} to {showDetail.endDate} | {showDetail.isActive ? 'Active' : 'Inactive'}</p>

            <div className="flex-between mb-10">
              <h4>Reward Catalog</h4>
              <button className="btn btn-sm btn-primary" onClick={openAddCatalog}><FiPlus /> Add Item</button>
            </div>

            <div className="table-container">
              <table>
                <thead><tr><th>Item</th><th>Points</th><th>Stock</th><th>User Type</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {showDetail.catalogItems?.map((item: any) => (
                    <tr key={item.catalogId}>
                      <td><strong>{item.itemName}</strong></td>
                      <td>{item.pointsRequired}</td>
                      <td>{item.stockQty}</td>
                      <td><span className="badge badge-info">{item.userType}</span></td>
                      <td><span className={`badge ${item.isActive ? 'badge-success' : 'badge-danger'}`}>{item.isActive ? 'Yes' : 'No'}</span></td>
                      <td>
                        <div className="flex gap-10">
                          <button className="btn btn-sm btn-outline" onClick={() => openEditCatalog(item)}><FiEdit2 /></button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCatalog(item.catalogId)}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!showDetail.catalogItems || showDetail.catalogItems.length === 0) &&
                    <tr><td colSpan={6} className="text-center">No catalog items</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Catalog Item Modal */}
      {showCatalogModal && (
        <div className="modal-overlay" onClick={() => setShowCatalogModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCatalog ? 'Edit Catalog Item' : 'Add Catalog Item'}</h3>
              <button className="modal-close" onClick={() => setShowCatalogModal(false)}>&times;</button>
            </div>
            <div className="form-group">
              <label>Item Name *</label>
              <input className="form-control" value={catalogForm.itemName} onChange={(e) => setCatalogForm({ ...catalogForm, itemName: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-control" rows={2} value={catalogForm.description} onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Image URL</label>
              <input className="form-control" value={catalogForm.imageUrl} onChange={(e) => setCatalogForm({ ...catalogForm, imageUrl: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Points Required *</label>
                <input className="form-control" type="number" value={catalogForm.pointsRequired}
                  onChange={(e) => setCatalogForm({ ...catalogForm, pointsRequired: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Stock Quantity</label>
                <input className="form-control" type="number" value={catalogForm.stockQty}
                  onChange={(e) => setCatalogForm({ ...catalogForm, stockQty: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>User Type</label>
                <select className="form-control" value={catalogForm.userType}
                  onChange={(e) => setCatalogForm({ ...catalogForm, userType: e.target.value })}>
                  <option value="both">Both</option>
                  <option value="dealer">Dealer Only</option>
                  <option value="contractor">Contractor Only</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCatalogModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveCatalog}>{editingCatalog ? 'Update' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schemes;
